import React, { useState } from "react";
import {
  addDoc,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import db from "../../utils/firebase.config";
import {
  createGuestDraft,
  createWardDraft,
  ensureUniqueSlug,
  getFirestoreErrorMessage,
  getSlugSeedFromGuest,
  normalizeWards,
  slugify,
} from "./helpers";

const CreatePage = () => {
  const [familyAlias, setFamilyAlias] = useState("");
  const [guest, setGuest] = useState(createGuestDraft());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdInvitation, setCreatedInvitation] = useState(null);

  const baseUrl = window.location.origin;

  const updateGuestField = (field, value) => {
    setGuest((current) => {
      const next = { ...current, [field]: value };
      if (field === "name" && !next.slug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const updateWardField = (wardIndex, field, value) => {
    setGuest((current) => {
      const nextWards = [...current.wards];
      nextWards[wardIndex] = { ...nextWards[wardIndex], [field]: value };
      return { ...current, wards: nextWards };
    });
  };

  const addWard = () => {
    setGuest((current) => ({ ...current, wards: [...current.wards, createWardDraft()] }));
  };

  const removeWard = (wardIndex) => {
    setGuest((current) => ({
      ...current,
      wards: current.wards.filter((_, index) => index !== wardIndex),
    }));
  };

  const validate = () => {
    if (!familyAlias.trim()) {
      return "Debes capturar el alias de familia";
    }
    if (!guest.name.trim()) {
      return "Falta nombre del invitado";
    }
    if (!guest.mobile.trim() || !/^\d{10}$/.test(guest.mobile.trim())) {
      return "El teléfono del invitado debe tener 10 dígitos";
    }

    for (const [wardIndex, ward] of guest.wards.entries()) {
      if (!ward.name.trim()) {
        return `Falta nombre del acompañante #${wardIndex + 1}`;
      }
    }

    return "";
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      const duplicatedMobileQuery = query(
        collectionGroup(db, "guess"),
        where("mobile", "==", guest.mobile.trim())
      );
      const duplicatedMobileSnapshot = await getDocs(duplicatedMobileQuery);
      if (!duplicatedMobileSnapshot.empty) {
        setError("El número de teléfono ya existe en otra invitación");
        return;
      }

      const now = new Date().toISOString();
      const invitationCreated = await addDoc(collection(db, "invitation"), {
        familyAlias: familyAlias.trim(),
        createdAt: now,
        updatedAt: now,
      });

      const slug = ensureUniqueSlug(getSlugSeedFromGuest(guest), new Set());
      const guestPayload = {
        name: guest.name.trim(),
        slug,
        confirmed: guest.confirmed === true,
        mobile: guest.mobile.trim(),
        table: (guest.table || "").trim(),
        wards: normalizeWards(guest.wards),
        createdAt: now,
        updateAt: now,
        updatedAt: now,
      };

      const guestCreated = await addDoc(
        collection(db, `invitation/${invitationCreated.id}/guess`),
        guestPayload
      );

      const created = {
        id: invitationCreated.id,
        familyAlias: familyAlias.trim(),
        guests: [{ id: guestCreated.id, ...guestPayload }],
      };

      setCreatedInvitation(created);
      setSuccess("Invitación creada correctamente");
      setFamilyAlias("");
      setGuest(createGuestDraft());
    } catch (saveError) {
      console.error("create invitation error", saveError);
      setError(getFirestoreErrorMessage(saveError));
    } finally {
      setLoading(false);
    }
  };

  const getInvitationLink = (invitationId, guestData) =>
    `${baseUrl}/invitacion/${guestData.slug}/${invitationId}`;

  const getQrValidationLink = (invitationId, guestId, wardId = "null") =>
    `${baseUrl}/confirmacionqr/${invitationId}/${guestId}/${wardId}`;

  return (
    <div className="admin-page">
      <div className="admin-section">
        <h3>Crear Invitación</h3>
        <p>
          Esta pantalla crea una invitación independiente por guardado
          (familia + invitado principal + acompañantes).
        </p>
      </div>

      <form className="admin-form" onSubmit={handleCreate}>
        <div className="field-block">
          <label htmlFor="familyAlias">Alias de familia</label>
          <input
            id="familyAlias"
            type="text"
            value={familyAlias}
            onChange={(e) => setFamilyAlias(e.target.value)}
            placeholder="Ej. Fam. García López"
          />
        </div>

        <div className="guest-card">
          <div className="guest-card-header">
            <h4>Invitado principal</h4>
          </div>

          <div className="guest-grid">
            <input
              type="text"
              value={guest.name}
              onChange={(e) => updateGuestField("name", e.target.value)}
              placeholder="Nombre"
            />
            <input
              type="text"
              value={guest.slug}
              onChange={(e) => updateGuestField("slug", slugify(e.target.value))}
              placeholder="Slug"
            />
            <input
              type="text"
              value={guest.mobile}
              maxLength={10}
              inputMode="numeric"
              onChange={(e) =>
                updateGuestField("mobile", e.target.value.replace(/\D/g, ""))
              }
              placeholder="Teléfono (10 dígitos)"
            />
            <input
              type="text"
              value={guest.table}
              onChange={(e) => updateGuestField("table", e.target.value)}
              placeholder="Mesa (opcional)"
            />
          </div>

          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={guest.confirmed}
              onChange={(e) => updateGuestField("confirmed", e.target.checked)}
            />
            Confirmado
          </label>

          <div className="wards-section">
            <div className="wards-header">
              <h5>Acompañantes</h5>
              <button type="button" className="btn-secondary" onClick={addWard}>
                Agregar acompañante
              </button>
            </div>

            {guest.wards.length === 0 && <p className="ward-empty">Sin acompañantes</p>}

            {guest.wards.map((ward, wardIndex) => (
              <div className="ward-row" key={`ward-${wardIndex}`}>
                <input
                  type="text"
                  value={ward.name}
                  onChange={(e) => updateWardField(wardIndex, "name", e.target.value)}
                  placeholder="Nombre acompañante"
                />
                <input
                  type="text"
                  value={ward.table}
                  onChange={(e) => updateWardField(wardIndex, "table", e.target.value)}
                  placeholder="Mesa (opcional)"
                />
                <label className="checkbox-line">
                  <input
                    type="checkbox"
                    checked={ward.confirmed}
                    onChange={(e) =>
                      updateWardField(wardIndex, "confirmed", e.target.checked)
                    }
                  />
                  Confirmado
                </label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => removeWard(wardIndex)}
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="action-row">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Guardando..." : "Guardar invitación"}
          </button>
        </div>

        {error && <p className="status error">{error}</p>}
        {success && <p className="status success">{success}</p>}
      </form>

      {createdInvitation && (
        <div className="saved-block">
          <h3>Última invitación creada</h3>
          <div className="invitation-card">
            <h4>{createdInvitation.familyAlias}</h4>
            <p>
              <strong>Invitation ID:</strong> {createdInvitation.id}
            </p>

            {createdInvitation.guests.map((guestData) => (
              <div className="saved-guest" key={guestData.id}>
                <p>
                  <strong>Invitado:</strong> {guestData.name}
                </p>
                <p>
                  <strong>Guess ID:</strong> {guestData.id}
                </p>
                <p>
                  <strong>Tel:</strong> {guestData.mobile || "-"} | <strong>Mesa:</strong>{" "}
                  {guestData.table || "-"} | <strong>Confirmado:</strong>{" "}
                  {guestData.confirmed ? "Sí" : "No"}
                </p>
                <p>
                  <strong>Slug:</strong> {guestData.slug}
                </p>
                <p>
                  <strong>Link invitación:</strong>{" "}
                  <a href={getInvitationLink(createdInvitation.id, guestData)}>
                    {getInvitationLink(createdInvitation.id, guestData)}
                  </a>
                </p>
                <p>
                  <strong>Link QR invitado:</strong>{" "}
                  <a href={getQrValidationLink(createdInvitation.id, guestData.id)}>
                    {getQrValidationLink(createdInvitation.id, guestData.id)}
                  </a>
                </p>

                {(guestData.wards || []).length > 0 && (
                  <div className="saved-wards">
                    <p>
                      <strong>Acompañantes:</strong>
                    </p>
                    {(guestData.wards || []).map((ward) => (
                      <div
                        className="saved-ward-line"
                        key={`created-ward-${guestData.id}-${ward.id}`}
                      >
                        <p>
                          #{ward.id} {ward.name} | Mesa: {ward.table || "-"} |
                          Confirmado: {ward.confirmed ? "Sí" : "No"}
                        </p>
                        <p>
                          <strong>Link QR acompañante:</strong>{" "}
                          <a
                            href={getQrValidationLink(
                              createdInvitation.id,
                              guestData.id,
                              ward.id
                            )}
                          >
                            {getQrValidationLink(
                              createdInvitation.id,
                              guestData.id,
                              ward.id
                            )}
                          </a>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePage;
